import React, { useEffect } from "react";
import RootLayout from "./components/RootLayout";
import MaatwerkLayout from "./components/maatwerk/MaatwerkLayout";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material";
import { Auth0Provider } from "@auth0/auth0-react";
import { createTheme } from "@mui/material";
import "@fontsource/lato";
import "@fontsource/figtree";
import { CssBaseline } from "@mui/material";
import MijnChecks from "./pages/MijnChecks";
import NieuweCheck from "./pages/NieuweCheck";
import NieuweCheckWH from "./pages/NieuweCheckWH";
import NieuweCheckWK from "./pages/NieuweCheckWK";
import NieuweCheckArbeid from "./pages/NieuweCheckArbeid";
import NieuweCheckOccasionAuto from "./pages/OccasionAuto/NieuweCheckOccasionAuto";
import Callback from "./pages/Callback";
import Klantenservice from "./pages/KlantenService";
import Account from "./pages/Account";
import Resultaten from "./pages/Resultaten";
import EmailNotVerified from "./pages/EmailNotVerified";
import "@fontsource/figtree/300.css";
import "@fontsource/figtree/400.css";
import "@fontsource/figtree/500.css";
import "@fontsource/figtree/600.css";
import "@fontsource/figtree/700.css";
import "@fontsource/figtree/800.css";
import "./fonts/fonts.css";
import "./App.css";
import NieuweCheckAS from "./pages/AbonnementSportschool/NieuweCheckAS";
import Voorwaardengids from "./pages/Voorwaardengids";
import MaatwerkUpload from "./components/maatwerk/purchase-contract/MaatwerkUpload";
import MaatwerkIntro from "./components/maatwerk/MaatwerkIntro";
import MaatwerkResultaten from "./components/maatwerk/MaatwerkResultaten";
import MaatwerkQuestionnaire from "./components/maatwerk/MaatwerkQuestionnaire";
import DemoLayout from "./components/demo/DemoLayout";
import DemoHome from "./components/demo/DemoHome";
import DemoUploadKoop from "./components/demo/DemoUploadKoop";
import DemoUploadHuur from "./components/demo/DemoUploadHuur";
import WelocateListings from "./components/welocate/WelocateListings";
import CheckinForm from "./components/welocate/CheckinForm";
import RoomForm from "./components/welocate/RoomForm";
import MeterForm from "./components/welocate/MeterForm";
import KeysForm from "./components/welocate/KeysForm";
import SignoffForm from "./components/welocate/SignoffForm";
import ViewingApplication from "./components/welocate/ViewingApplication";
import ViewingApplicationListings from "./components/welocate/ViewingApplicationListings";
import AdminAuth from "./components/welocate/admin/AdminAuth";
import AdminApplications from "./components/welocate/admin/AdminApplications";
import AdminApplicationDetail from "./components/welocate/admin/AdminApplicationDetail";
import AdminStaticPage from "./components/welocate/admin/AdminStaticPage";
import AdminValidationRules from "./components/welocate/admin/AdminValidationRules";

const REACT_APP_AUTH0_CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID;

// Auth0-configuratie
// const AUTH0_DOMAIN = "dev-fxf5jxgmrowrkemd.eu.auth0.com";
const AUTH0_DOMAIN = "auth.checkyourterms.com";
const AUTH0_CLIENT_ID = REACT_APP_AUTH0_CLIENT_ID;

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#439BD9",
    },
    secondary: {
      main: "#E27C00",
    },
    tertiary: {
      main: "#DCEDFA",
    },
    error: {
      main: "#FF3333",
    },
    warning: {
      main: "#ffcc00",
    },
    success: {
      main: "#33cc33",
    },
    background: {
      default: "#ebebeb",
      paper: "#ebebeb",
    },
  },
  shadows: {
    0: "0px 0px 0px",
    1: "0px 0px 5px 2px rgba(0, 0, 0, 0.15)",
    4: "",
    8: "",
    24: "",
  },

  typography: {
    fontFamily: "Figtree",
    body1: {
      fontFamily: "Figtree",
      fontWeight: 300,
      fontSize: "12px",
    },
    h1: {
      fontFamily: "Figtree",
      color: "#062840",
      fontWeight: 800,
      fontSize: "56px",
      lineHeight: "136%",
    },
    h2: { fontFamily: "Figtree", color: "#2D2D2D" },
    h3: {
      fontFamily: "Figtree",
      color: "#0F3A58",
      fontWeight: 700,
      fontSize: "32px",
      lineHeight: "180%",
    },
    h4: {
      fontFamily: "Figtree",
      color: "#0F3A58",
      fontWeight: 700,
      fontSize: "28px",
      lineHeight: "120%",
    },
    h5: {
      fontFamily: "Figtree",
      color: "#0F3A58",
      fontWeight: 500,
      fontSize: "24px",
    },
    h6: {
      fontFamily: "Figtree",
      color: "#032B4A",
      fontWeight: 400,
      fontSize: "16px",
    },
  },
  breakpoints: {
    values: {
      mobile: 0,
      ipad: 650,
      laptop: 1080,
    },
  },
});

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    id: "root",
    errorElement: <div>Something went wrong</div>,
    children: [
      {
        path: "/",
        element: <MijnChecks />,
      },
      {
        path: "/modules",
        element: <NieuweCheck />,
      },
      {
        path: "/customer-service",
        element: <Klantenservice />,
      },
      {
        path: "/new-check",
        element: <NieuweCheck />,
      },
      {
        path: "/new-check/housing/rent",
        element: <NieuweCheckWH />,
      },
      {
        path: "/new-check/housing/buy",
        element: <NieuweCheckWK />,
      },
      {
        path: "/new-check/housing/student_rent",
        element: <NieuweCheckWH />,
      },
      {
        path: "/new-check/career/labour-contract",
        element: <NieuweCheckArbeid />,
      },
      {
        path: "/new-check/transport/car-occasion",
        element: <NieuweCheckOccasionAuto />,
      },
      {
        path: "/new-check/leisure-and-sports/gym-contract",
        element: <NieuweCheckAS />,
      },
      {
        path: "/voorwaardengids",
        element: <Voorwaardengids />,
      },
      {
        path: "/account-settings",
        element: <Account />,
      },
      {
        path: "/results/:moduleId/:runId",
        element: <Resultaten />,
      },
    ],
  },
  {
    path: "/callback",
    element: <Callback />,
  },
  {
    path: "/email-not-verified",
    element: <EmailNotVerified />,
  },
]);

const WL_FONT = '"Urbanist", ui-sans-serif, system-ui, -apple-system, sans-serif';
const WL_HEADING_FONT = '"ALT Gumbo", "Urbanist", ui-sans-serif, system-ui, -apple-system, sans-serif';

const welocateTheme = createTheme({
  ...theme,
  typography: {
    ...theme.typography,
    fontFamily: WL_FONT,
    fontWeightRegular: 400,
    // Urbanist: body text, subtitles, captions
    body1: { ...theme.typography.body1, fontFamily: WL_FONT, fontWeight: 400 },
    body2: { fontFamily: WL_FONT, fontWeight: 400 },
    subtitle1: { fontFamily: WL_FONT, fontWeight: 400 },
    subtitle2: { fontFamily: WL_FONT, fontWeight: 400 },
    caption: { fontFamily: WL_FONT, fontWeight: 400 },
    // ALT Gumbo: titles and uppercase subtitles
    h1: { ...theme.typography.h1, fontFamily: WL_HEADING_FONT, fontWeight: 500 },
    h2: { ...theme.typography.h2, fontFamily: WL_HEADING_FONT, fontWeight: 500 },
    h3: { ...theme.typography.h3, fontFamily: WL_HEADING_FONT, fontWeight: 500 },
    h4: { ...theme.typography.h4, fontFamily: WL_HEADING_FONT, fontWeight: 500 },
    h5: { ...theme.typography.h5, fontFamily: WL_HEADING_FONT, fontWeight: 500 },
    h6: { ...theme.typography.h6, fontFamily: WL_HEADING_FONT, fontWeight: 500 },
    overline: {
      fontFamily: WL_HEADING_FONT,
      fontWeight: 500,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
    },
  },
  components: {
    // ALT Gumbo for quotes
    MuiCssBaseline: {
      styleOverrides: {
        blockquote: {
          fontFamily: WL_HEADING_FONT,
          fontWeight: 500,
        },
      },
    },
  },
});

// Check if we're on the maatwerk subdomain
const isMaatwerkSubdomain =
  window.location.hostname === "maatwerk.checkyourterms.com" ||
  window.location.hostname === "maatwerk.localhost";

const isWelocateSubdomain =
  window.location.hostname === "welocate.checkyourterms.com" ||
  window.location.hostname === "welocate.localhost";

const isDemoSubdomain =
  window.location.hostname === "demo.checkyourterms.com" ||
  window.location.hostname === "demo.localhost";

// Router for maatwerk subdomain
const demoRouter = createBrowserRouter([
  {
    path: "/",
    element: <DemoLayout />,
    children: [
      {
        path: "/",
        element: <DemoHome />,
      },
      {
        // Upload + payment flow; Mollie redirects back to
        // /upload?order_id=<uuid> after checkout.
        path: "/upload",
        element: <DemoUploadKoop />,
      },
      {
        path: "/koopakte",
        element: (
          <MaatwerkIntro module="PURCHASE" basePath="/koopakte" showPricing />
        ),
      },
      {
        // Same Mollie-enabled upload page as /upload; the intro funnel
        // navigates here.
        path: "/koopakte/upload",
        element: <DemoUploadKoop />,
      },
      {
        path: "/koopakte/results/:requestId",
        element: <MaatwerkResultaten />,
      },
      // {
      //   path: "/koopakte/questionnaire/:referenceId",
      //   element: <MaatwerkQuestionnaire />,
      // },
      {
        path: "/huurovereenkomst",
        element: (
          <MaatwerkIntro
            module="RENTAL"
            basePath="/huurovereenkomst"
            showPricing
          />
        ),
      },
      {
        // Same Mollie-enabled upload page as the koop flow, rental variant.
        path: "/huurovereenkomst/upload",
        element: <DemoUploadHuur />,
      },
      {
        path: "/huurovereenkomst/results/:requestId",
        element: <MaatwerkResultaten />,
      },
      // {
      //   path: "/huurovereenkomst/questionnaire/:referenceId",
      //   element: <MaatwerkQuestionnaire />,
      // },
    ],
  },
]);

const welocateRouter = createBrowserRouter([
  {
    path: "/rental",
    element: <MaatwerkLayout company="WELOCATE" />,
    children: [
      {
        index: true,
        element: <MaatwerkIntro module="RENTAL" />,
      },
      {
        path: "upload",
        element: <MaatwerkUpload module="RENTAL" />,
      },
      {
        path: "results/:requestId",
        element: <MaatwerkResultaten />,
      },
      {
        path: "questionnaire/:referenceId",
        element: <MaatwerkQuestionnaire />,
      },


    ],
  },
  {
    path: "/listings-demo",
    element: <WelocateListings />,
  },
  {
    path: "checkin/:cfid",
    element: <CheckinForm />
  },
  {
    path: "checkin/:cfid/meters",
    element: <MeterForm />,
  },
  {
    path: "checkin/:cfid/keys",
    element: <KeysForm />,
  },
  {
    path: "checkin/:cfid/signoff",
    element: <SignoffForm />,
  },
  {
    path: "checkin/:cfid/rooms/:roomId",
    element: <RoomForm />
  },
  {
    path: "viewing_application",
    element: <ViewingApplicationListings />,
  },
  {
    path: "viewing_application/:whRefId",
    element: <ViewingApplication />
  },
  {
    path: "/admin",
    element: <AdminAuth />,
    children: [
      {
        index: true,
        element: <Navigate to="/admin/applications" replace />,
      },
      {
        path: "listings",
        element: (
          <AdminStaticPage title="Listings" subtitle="12 listed properties" />
        ),
      },
      {
        path: "check-ins",
        element: (
          <AdminStaticPage title="Check-ins" subtitle="12 scheduled check-ins" />
        ),
      },
      {
        path: "applications",
        element: <AdminApplications />,
      },
      {
        path: "applications/:applicationId",
        element: <AdminApplicationDetail />,
      },
      {
        path: "validation-rules",
        element: <AdminValidationRules />,
      },
      {
        path: "rental",
        element: (
          <AdminStaticPage
            title="Rental"
            subtitle="Rental contract checks via Check Your Terms"
          />
        ),
      },
      {
        path: "purchase",
        element: (
          <AdminStaticPage
            title="Purchase"
            subtitle="Purchase contract checks via Check Your Terms"
          />
        ),
      },
    ],
  },
]);

// Router for maatwerk subdomain
const maatwerkRouter = createBrowserRouter([
  {
    path: "/",
    element: <MaatwerkLayout company="MAATWERK" />,
    children: [
      {
        path: "/",
        element: <MaatwerkIntro module="PURCHASE" />,
      },
      {
        path: "/upload",
        element: <MaatwerkUpload />,
      },
      {
        path: "/results/:requestId",
        element: <MaatwerkResultaten />,
      },
      {
        path: "/questionnaire/:referenceId",
        element: <MaatwerkQuestionnaire />,
      },
    ],
  },
]);

// Then simplify the main App component
const App = () => {
  // Render placeholder for maatwerk subdomain
  if (isMaatwerkSubdomain) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RouterProvider router={maatwerkRouter} />
      </ThemeProvider>
    );
  } else if (isWelocateSubdomain) {
    return (
      <ThemeProvider theme={welocateTheme}>
        <CssBaseline />
        <RouterProvider router={welocateRouter} />
      </ThemeProvider>
    );
  } else if (isDemoSubdomain) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RouterProvider router={demoRouter} />
      </ThemeProvider>
    );
  }

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        // audience: "https://portal.checkyourterms.com/aud",
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: "openid profile email",
      }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RouterProvider router={router} />
      </ThemeProvider>
    </Auth0Provider>
  );
};

export default App;
